//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Fragment } from 'react'

import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import ExpandableTableRows from 'components/Shared/TableNew/ExpandableTableRows'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import EnabledIcon from 'components/Shared/EnabledIcon/Index'
import TableCells from './TableCells'
import ExpandedRowChildren from './ExpandedRowChildren'

import { RouteTicketProviderTicket } from 'routes'


const BulkDeleteHeaderCell = styled(TableCell)`
  && {
    padding: 6px 14px 6px 14px;
  }
`

// It is used in both TicketProviderTicketPage and TicketProviderTicketsPage
// - for withExpandableRows === true, row can be expanded if ticket supports hierarchy
//   and has children (for ticket page).
// - for withExpandableRows === false, row cannot be expanded even if ticket supports
//   hierarchy but children badge is shown if ticket has children (for tickets list page).
export const TicketsTable = ({
  tickets, selectedTicketId, onRowClick,
  partnerStatusesMap, searchValue,
  checkableRows, onCheckAllRowsToggle, checkedTicketIds, onRowCheck,
  withExpandableRows, providerTicketBoard,
}) => {
  const { t } = useTranslation()

  const isExpandableRow = (ticket) => {
    return (withExpandableRows && ticket.hierarchyEnabled && ticket.childTicketsCount)
  }

  const onRowClick_ = (ticket, onExpandToggle) => {
    const isExpandable = isExpandableRow(ticket)

    if (checkableRows) {
      onRowCheck(ticket.id, ticket.externalId)
    } else if (isExpandable) {
      onExpandToggle()
    } else {
      onRowClick?.(ticket)
    }
  }

  const ticketsTableConfig = providerTicketBoard?.ticketsTableConfig || []

  const onRenderRow = ({ item, isExpanded, onExpandToggle }) => {
    const ticket = item
    const statusName = ticket.status
    const statusColor = partnerStatusesMap[statusName]?.color

    const ticketUrl = RouteTicketProviderTicket({
      ticketProviderId: ticket.ticketProviderId,
      ticketId: ticket.externalId,
    })
    const isExpandable = isExpandableRow(ticket)

    return (
      <TableRow
        key={ticket.id}
        onClick={() => { onRowClick_(ticket, onExpandToggle) }}
        clickable={isExpandable}
        color={statusColor}
        active={selectedTicketId === ticket.id}
      >
        <TableCells
          ticket={ticket}
          checkedTicketIds={checkedTicketIds}
          ticketUrl={ticketUrl}
          partnerStatusesMap={partnerStatusesMap}
          searchValue={searchValue}
          checkableRows={checkableRows}
          withExpandableRows={withExpandableRows}
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
        />
      </TableRow>
    )
  }

  const onRenderExpandedRows = ({ item }) => {
    return (
      <ExpandedRowChildren ticket={item} />
    )
  }

  const extractFieldName = (fieldName) => {
    if (fieldName === 'id') {
      return t('ticketsPage.tableHeaderId')
    }

    if (fieldName === 'partner_id') {
      return t('ticketProviderTicketsPage.tableHeaderPartnerId')
    }

    return fieldName.replaceAll('_', ' ')
  }

  const renderProviderBoardHeader = () => {
    return (
      <>
        {ticketsTableConfig.map(field => {
          return (
            <Fragment key={field.name}>
              <TableCell>{extractFieldName(field.name)}</TableCell>
            </Fragment>
          )
        })}
      </>
    )
  }

  return (
    <Table width='100%'>
      <TableHead withTopBorder>
        <TableRow>
          {checkableRows && (
            <BulkDeleteHeaderCell align='center'>
              <EnabledIcon enabled onClick={onCheckAllRowsToggle} />
            </BulkDeleteHeaderCell>
          )}
          {renderProviderBoardHeader()}

        </TableRow>
      </TableHead>
      <ExpandableTableRows
        items={tickets}
        onRenderRow={onRenderRow}
        onRenderExpandedRows={onRenderExpandedRows}
      />
      {!tickets.length && (
        <TableBody>
          <TableRowNoData
            data={tickets}
            hasFilterOrSearch
          />
        </TableBody>
      )}
    </Table>
  )
}

TicketsTable.propTypes = {
  tickets: PropTypes.array.isRequired,
  selectedTicketId: PropTypes.string,
  onRowClick: PropTypes.func,
  partnerStatusesMap: PropTypes.object.isRequired,
  searchValue: PropTypes.string,
  checkableRows: PropTypes.bool,
  onCheckAllRowsToggle: PropTypes.func,
  checkedTicketIds: PropTypes.array,
  onRowCheck: PropTypes.func,
  withExpandableRows: PropTypes.bool,
  providerTicketBoard: PropTypes.object,
}

export default TicketsTable
